<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import DestructiveConfirmDialog from '~/components/app/DestructiveConfirmDialog.vue'
import DiscardUnsavedDialog from '~/components/app/DiscardUnsavedDialog.vue'
import EntityFormFooter from '~/components/Forms/EntityFormFooter.vue'
import FormOverlaySkeleton from '~/components/app/FormOverlaySkeleton.vue'
import SourceSelect from '~/components/ui/SourceSelect.vue'
import SourceInput from '~/components/ui/SourceInput.vue'
import SourceRichTextEditor from '~/components/ui/SourceRichTextEditor.vue'

import { useAgences } from '~/composables/orga/useAgence'
import { useConcurrentForm } from '~/composables/orga/useConcurrentForm'
import OrganizationConflictDialog from '~/components/Forms/OrganizationConflictDialog.vue'
import { getErrorMessage, normalizeString } from '~/composables/ui/useFormError'
import { useFormValidationFeedback } from '~/composables/ui/useFormValidationFeedback'
import { useDiscardUnsavedGuard } from '~/composables/ui/useDiscardUnsavedGuard'
import { useFormDirtySnapshot } from '~/composables/ui/useFormDirtySnapshot'
import { useSourceToast } from '~/composables/ui/useSourceToast'
import { Agence } from '~/models/Agence'
import { useOrgEditActor } from '~/composables/auth/useOrgEditActor'
import { actorMayDeleteAgence } from '~~/utils/auth/orgEditAffordances'

type AgenceItem = {
  dirLib: string
  domLib: string
  ageLib: string
  ageLibLong?: string | null
  ageDesc?: string | null
}

type Props = {
  showOverlay?: boolean
  directionName: string
  domainesWithAgences?: Record<string, AgenceItem[]>
  isEditing?: boolean
  /** Domaine parent présélectionné quand on ouvre depuis une colonne (ex. test2) */
  defaultDomLib?: string
  /** En mode édition : agence à présélectionner (ageLib) pour ouvrir directement sur cette agence */
  initialSelectedAgence?: string
}

const props = withDefaults(defineProps<Props>(), {
  showOverlay: false,
  isEditing: false,
  domainesWithAgences: () => ({}),
  defaultDomLib: '',
  initialSelectedAgence: '',
})

const { actor } = useOrgEditActor()
const mayDeleteAgence = computed(() =>
  actorMayDeleteAgence(actor.value, props.directionName, props.defaultDomLib),
)

const emit = defineEmits<{
  (e: 'close' | 'refetch'): void
}>()

const hasUnsavedChanges = defineModel<boolean>('hasUnsavedChanges', { default: false })

const { storeAgence, mergeAgence, deleteAgence } = useAgences()
const concurrent = useConcurrentForm()
const toast = useSourceToast()
const feedback = useFormValidationFeedback({
  fieldOrder: ['domLib', 'selectedAgence', 'ageLib', 'ageLibLong', 'ageDesc'],
})

const loading = ref(false)
const loadDone = ref(true)
const showDeleteConfirm = ref(false)

const selectedAgence = ref('')

const agence = ref({
  dirLib: props.directionName,
  domLib: props.defaultDomLib || '',
  ageLib: '',
  ageLibLong: '',
  ageDesc: '',
})

const formSnapshot = computed(() => ({
  ...agence.value,
  selectedAgence: selectedAgence.value,
}))

const showOverlayRef = computed(() => props.showOverlay)

const { captureSnapshot } = useFormDirtySnapshot({
  showOverlay: showOverlayRef,
  currentValue: formSnapshot,
  hasUnsavedChanges,
})

const resetForm = () => {
  agence.value = {
    dirLib: props.directionName,
    domLib: props.defaultDomLib || '',
    ageLib: '',
    ageLibLong: '',
    ageDesc: '',
  }
  selectedAgence.value = ''
  feedback.clearAll()
  loadDone.value = true
}

watch(
    () => [props.directionName, props.defaultDomLib] as const,
    ([dir, defaultDom]) => {
      agence.value.dirLib = dir ?? props.directionName
      agence.value.domLib = defaultDom ?? ''
      if (!(props.isEditing && props.initialSelectedAgence)) {
        agence.value.ageLib = ''
        agence.value.ageLibLong = ''
        agence.value.ageDesc = ''
        selectedAgence.value = ''
      }
      feedback.clearAll()
    },
)

watch(
    () => [props.defaultDomLib, props.initialSelectedAgence] as const,
    ([defaultDom, initialAge]) => {
      if (!props.isEditing || !defaultDom || !initialAge) return
      agence.value.domLib = defaultDom
      selectedAgence.value = initialAge
    },
    { immediate: true }
)

// Remplir les champs depuis domainesWithAgences quand on ouvre en modification avec une agence présélectionnée
watch(
    () => [props.domainesWithAgences, props.defaultDomLib, props.initialSelectedAgence] as const,
    ([domaines, defaultDom, initialAge]) => {
      if (!props.isEditing || !defaultDom || !initialAge) {
        loadDone.value = true
        return
      }
      loadDone.value = false
      const dir = props.directionName
      const key = `${dir}~${defaultDom}`
      const found = (domaines?.[key] ?? []).find((a) => a.ageLib === initialAge)
      if (!found) {
        loadDone.value = true
        return
      }
      agence.value = {
        dirLib: found.dirLib,
        domLib: found.domLib,
        ageLib: found.ageLib,
        ageLibLong: (found as { ageLibLong?: string; ageDesc?: string }).ageLibLong ?? '',
        ageDesc: (found as { ageLibLong?: string; ageDesc?: string }).ageDesc ?? '',
      }
      selectedAgence.value = initialAge
      concurrent.rememberBase({ ...agence.value })
      setTimeout(() => {
        loadDone.value = true
        captureSnapshot()
      }, 350)
    },
    { immediate: true }
)

watch(
  () => props.showOverlay,
  (show) => {
    if (!show || props.isEditing) return
    captureSnapshot()
  },
)

// domaines disponibles (keys = dir~dom)
const domainesOptions = computed(() => {
  return Object.keys(props.domainesWithAgences || {})
    .map((key) => key.split('~')[1])
    .filter((domLib): domLib is string => Boolean(domLib))
})

// liste agences d’un domaine
const agencesOptions = (domaineKey: string) => {
  const list = props.domainesWithAgences?.[domaineKey] ?? []
  return list.map((a) => a.ageLib)
}

// préremplir agence quand sélectionnée
watch(selectedAgence, (val) => {
  if (!props.isEditing || !val) return

  const dir = agence.value.dirLib
  const dom = agence.value.domLib
  const key = `${dir}~${dom}`

  const found = (props.domainesWithAgences?.[key] ?? []).find((a) => a.ageLib === val)
  if (!found) return

  agence.value = {
    dirLib: found.dirLib,
    domLib: found.domLib,
    ageLib: found.ageLib,
    ageLibLong: (found as { ageLibLong?: string; ageDesc?: string }).ageLibLong ?? '',
    ageDesc: (found as { ageLibLong?: string; ageDesc?: string }).ageDesc ?? '',
  }
  concurrent.rememberBase({ ...agence.value })
})

function discardAndClose() {
  hasUnsavedChanges.value = false
  emit('close')
  resetForm()
  hasUnsavedChanges.value = false
}

const { showDiscardConfirm, requestClose, confirmDiscard, cancelDiscard } = useDiscardUnsavedGuard({
  isDirty: computed(() => hasUnsavedChanges.value),
  onDiscard: discardAndClose,
  loading,
})

function rejectClientField(field: string, message: string): false {
  feedback.setFieldError(field, message)
  feedback.notifySubmitFailure('client')
  feedback.focusFirstInvalid()
  return false
}

function assertAgenceClientValid(): boolean {
  if (!agence.value.domLib) {
    return rejectClientField('domLib', 'Veuillez sélectionner un domaine.')
  }
  if (props.isEditing && !selectedAgence.value) {
    return rejectClientField('selectedAgence', 'Veuillez sélectionner une agence à modifier.')
  }
  return true
}

function applyNormalizedAgenceFields(requireAgeLib: boolean): boolean {
  const ageLib = normalizeString(agence.value.ageLib) || (requireAgeLib ? '' : agence.value.ageLib)
  const ageLibLong = normalizeString(agence.value.ageLibLong) || undefined
  const ageDesc = normalizeString(agence.value.ageDesc) || undefined

  if (requireAgeLib && !ageLib) {
    return rejectClientField('ageLib', "L'acronyme de l'agence est obligatoire.")
  }

  agence.value.ageLib = ageLib
  if (ageLibLong !== undefined) agence.value.ageLibLong = ageLibLong
  if (ageDesc !== undefined) agence.value.ageDesc = ageDesc
  return true
}

async function persistEditedAgence(): Promise<boolean> {
  const origin = concurrent.savedBase.value
  const key = `${String(origin?.dirLib ?? agence.value.dirLib)}~${String(origin?.domLib ?? agence.value.domLib)}~${selectedAgence.value}`
  if (!applyNormalizedAgenceFields(false)) return false
  const envelope = concurrent.buildPatch(agence.value, ['dirLib', 'domLib', 'ageLib', 'ageLibLong', 'ageDesc'])
  if (envelope === null) {
    toast.success('Agence modifiée', `« ${agence.value.ageLib} » a été mise à jour.`)
    return true
  }
  const saved = await mergeAgence(key, envelope.changes, envelope.base)
  const renamed = await concurrent.noteParentRename(key, saved?.currentKey, envelope.changes)
  if (!renamed) {
    toast.success('Agence modifiée', `« ${agence.value.ageLib} » a été mise à jour.`)
  }
  return true
}

async function persistCreatedAgence(): Promise<boolean> {
  if (!applyNormalizedAgenceFields(true)) return false
  await storeAgence(new Agence(agence.value))
  toast.success('Agence créée', `« ${agence.value.ageLib} » a été ajoutée.`)
  return true
}

async function persistAgence(): Promise<boolean> {
  if (!assertAgenceClientValid()) return false
  if (props.isEditing) return persistEditedAgence()
  return persistCreatedAgence()
}

function handleAgenceSubmitError(error: unknown): void {
  if (concurrent.captureError(error)) return
  if (feedback.applyFromUnknown(error)) {
    feedback.notifySubmitFailure('api')
    feedback.focusFirstInvalid()
    return
  }
  feedback.notifyNonFieldError('Enregistrement impossible', getErrorMessage(error))
}

async function validateForm() {
  feedback.clearAll()
  loading.value = true

  try {
    if (!(await persistAgence())) return
    emit('refetch')
    discardAndClose()
  } catch (e: unknown) {
    handleAgenceSubmitError(e)
  } finally {
    loading.value = false
  }
}

const agenceToDeleteKey = computed(() => {
  if (!props.isEditing || !agence.value.domLib || !selectedAgence.value) return ''
  return `${agence.value.dirLib}~${agence.value.domLib}~${selectedAgence.value}`
})

const agenceDeleteConfirmPhrase = computed(() => {
  return agence.value.ageLib || selectedAgence.value || ''
})

function confirmConflict(): void {
  concurrent.acceptChoices(agence.value)
  void validateForm()
}

function restoreFromTrash(): void {
  concurrent.closeConflict()
  void navigateTo('/admin/organigram-trash')
}

async function onConfirmDelete() {
  if (!agenceToDeleteKey.value) return
  feedback.clearAll()
  loading.value = true
  try {
    const ok = await deleteAgence(agenceToDeleteKey.value)
    if (ok) {
      showDeleteConfirm.value = false
      toast.success('Agence supprimée', 'L’agence a été retirée de l’organigramme.')
      emit('refetch')
      discardAndClose()
    } else {
      feedback.notifyNonFieldError('Suppression impossible', "Impossible de supprimer l'agence.")
    }
  } catch (e: unknown) {
    feedback.notifyNonFieldError('Suppression impossible', getErrorMessage(e))
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="form-content">
    <!-- Skeleton en overlay pendant le chargement (mode édition avec agence présélectionnée) -->
    <div
      v-if="isEditing && !loadDone"
      class="form-skeleton-overlay"
    >
      <FormOverlaySkeleton aria-label="Chargement de l'agence" />
    </div>

    <div class="form-body">
    <p class="info-text">
      {{ isEditing ? "Modification d'une agence" : "Création d'une nouvelle agence" }} dans le domaine :
      <span class="highlight">{{ agence.domLib || '-' }}</span>
    </p>

    <div class="content">
      <SourceSelect
          name="domLib"
          label="Sélectionner un domaine *"
          :options="domainesOptions"
          :selected-option="agence.domLib"
          size="medium"
          :error="feedback.fieldError('domLib')"
          @update:selected-option="(val) => { agence.domLib = val; feedback.clearFieldError('domLib') }"
      />

      <SourceSelect
          v-if="isEditing && !initialSelectedAgence"
          name="selectedAgence"
          label="Sélectionnez une agence"
          :options="agencesOptions(`${directionName}~${agence.domLib}`)"
          :selected-option="selectedAgence"
          :disabled="!agence.domLib"
          size="medium"
          :error="feedback.fieldError('selectedAgence')"
          @update:selected-option="(val) => { selectedAgence = val; feedback.clearFieldError('selectedAgence') }"
      />

      <SourceInput
          :model-value="agence.ageLib"
          name="ageLib"
          label="Acronyme de l'agence *"
          placeholder="Entrez l'acronyme de l'agence"
          size="medium"
          type="text"
          :error="feedback.fieldError('ageLib')"
          @update:model-value="(v) => { agence.ageLib = v; feedback.clearFieldError('ageLib') }"
      />

      <SourceInput
          :model-value="agence.ageLibLong"
          name="ageLibLong"
          label="Nom de l'agence"
          placeholder="Entrez le nom de l'agence"
          size="medium"
          type="text"
          :error="feedback.fieldError('ageLibLong')"
          @update:model-value="(v) => { agence.ageLibLong = v; feedback.clearFieldError('ageLibLong') }"
      />

      <SourceRichTextEditor
          :model-value="agence.ageDesc"
          name="ageDesc"
          label="Description"
          placeholder="Ajoutez une description"
          size="medium"
          :error="feedback.fieldError('ageDesc')"
          @update:model-value="(v) => { agence.ageDesc = v; feedback.clearFieldError('ageDesc') }"
      />
    </div>
    </div>

    <EntityFormFooter
      :show-delete="isEditing && mayDeleteAgence"
      :delete-disabled="loading || !agenceToDeleteKey || !loadDone"
      :cancel-disabled="loading"
      :submit-disabled="loading || (isEditing && !loadDone)"
      :submit-label="isEditing ? 'Enregistrer' : 'Créer'"
      @delete="showDeleteConfirm = true"
      @cancel="requestClose"
      @submit="validateForm"
    />

    <DestructiveConfirmDialog
      v-model:open="showDeleteConfirm"
      title="Supprimer l'agence"
      :description="`Êtes-vous sûr de vouloir supprimer l'agence « ${agenceDeleteConfirmPhrase} » ? Cette action est irréversible.`"
      :confirm-phrase="agenceDeleteConfirmPhrase"
      :loading="loading"
      @confirm="onConfirmDelete"
    />

    <OrganizationConflictDialog
      :open="concurrent.conflict.value !== null || concurrent.deletedOpen.value"
      :deleted="concurrent.deletedOpen.value"
      :fields="concurrent.conflict.value?.fields ?? []"
      :choices="concurrent.choices.value"
      @choose="(field, choice) => { concurrent.choices.value[field] = choice }"
      @confirm="confirmConflict"
      @close="concurrent.closeConflict()"
      @restore="restoreFromTrash"
    />

    <DiscardUnsavedDialog
      :show="showDiscardConfirm"
      @close="cancelDiscard"
      @confirm="confirmDiscard"
    />
  </div>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/components/entity-form" as *;
@use "~/assets/scss/partials/elpdl/colors" as c;

.form-skeleton-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  background: c.$enedis-neutral-10;
  padding: 8px;
  overflow-y: auto;
}
</style>
